/**
 * Phase 9.2 — Lead detail drawer.
 *
 * Opens from a details icon on each pipeline card. Shows:
 *  - AI-generated summary card (who / funnel position / next best action)
 *    with a regenerate button that respects the 50k token soft cap
 *  - Lead facts (email, source, score, status, advisor)
 *  - Activity timeline (most recent 10)
 *  - "Draft follow-up" button that opens FollowupDraftModal
 */
import React, { useEffect, useState } from 'react'
import { X, Loader2, Sparkles, RefreshCw, AlertTriangle, Mail, PenLine } from 'lucide-react'
import FollowupDraftModal from './FollowupDraftModal'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

function fmtTs(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toISOString().slice(0, 16).replace('T', ' ')
  } catch (_) {
    return iso
  }
}

export default function LeadDetailDrawer({ lead, adminKey, onClose }) {
  const [summary, setSummary] = useState(null)
  const [summaryState, setSummaryState] = useState('loading') // loading | ok | error | soft_cap
  const [summaryError, setSummaryError] = useState('')
  const [showFollowup, setShowFollowup] = useState(false)

  const fetchSummary = async (opts = {}) => {
    setSummaryState('loading')
    setSummaryError('')
    const params = new URLSearchParams({ admin_key: adminKey })
    if (opts.force) params.set('force', 'true')
    if (opts.confirmSoftCap) params.set('confirm_soft_cap', 'true')
    try {
      const res = await fetch(
        `${API_URL}/api/leads/${encodeURIComponent(lead.lead_id)}/summary?${params}`
      )
      if (res.status === 503) {
        setSummaryState('error')
        setSummaryError('AI features are disabled on this deployment.')
        return
      }
      const data = await res.json()
      if (data.requires_confirmation) {
        setSummary(data)
        setSummaryState('soft_cap')
        return
      }
      if (!data.ok) {
        setSummaryState('error')
        setSummaryError(data.error || 'Could not load summary.')
        return
      }
      setSummary(data)
      setSummaryState('ok')
    } catch (e) {
      setSummaryState('error')
      setSummaryError('Network error — try again.')
    }
  }

  useEffect(() => {
    if (lead?.lead_id && adminKey) fetchSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.lead_id])

  if (!lead) return null
  return (
    <>
      <div
        data-testid="lead-drawer-scrim"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(11,37,69,0.35)', zIndex: 60 }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        data-testid="lead-drawer"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: 480,
          background: 'var(--admin-ivory)',
          color: 'var(--admin-slate)',
          borderLeft: '1px solid var(--admin-border)',
          zIndex: 61,
          overflowY: 'auto',
          padding: 20,
          fontFamily: 'Inter,sans-serif',
          boxShadow: '-20px 0 60px rgba(11,37,69,0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, color: 'var(--admin-muted)' }}>Lead</div>
            <h2 style={{ fontFamily: 'Fraunces,serif', margin: '4px 0 0', color: 'var(--admin-navy)', fontSize: 22 }}>
              {lead.name || lead.lead_id}
            </h2>
            <div style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{lead.email || 'no email'} · {lead.source || 'direct'} · score {lead.score ?? 0}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="lead-drawer-close" style={{ background: 'transparent', border: 'none', color: 'var(--admin-muted)', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {/* AI Summary card */}
        <div
          data-testid="lead-summary-card"
          style={{
            background: 'var(--admin-surface)',
            border: '1px solid var(--admin-gold)',
            borderRadius: 10,
            padding: 14,
            marginTop: 16,
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--admin-gold)' }}>
              <Sparkles size={13} /> AI summary
            </div>
            <button
              type="button"
              onClick={() => fetchSummary({ force: true })}
              disabled={summaryState === 'loading'}
              data-testid="lead-summary-refresh"
              aria-label="Regenerate"
              title="Regenerate summary"
              style={{ background: 'transparent', border: 'none', color: 'var(--admin-muted)', cursor: 'pointer' }}
            >
              <RefreshCw size={14} className={summaryState === 'loading' ? 'animate-spin' : ''} />
            </button>
          </div>

          {summaryState === 'loading' && (
            <div style={{ fontSize: 12, color: 'var(--admin-muted)', textAlign: 'center', padding: '12px 0' }}>
              <Loader2 size={18} className="animate-spin" style={{ display: 'inline-block', marginRight: 6 }} />
              Summarizing…
            </div>
          )}

          {summaryState === 'soft_cap' && (
            <div style={{ fontSize: 12, color: '#92400E', background: '#FEF3C7', border: '1px solid #D97706', borderRadius: 6, padding: 10 }}>
              <AlertTriangle size={14} style={{ display: 'inline-block', marginRight: 4 }} />
              This lead has used {summary?.tokens_used?.toLocaleString()} AI tokens (soft cap {summary?.soft_cap?.toLocaleString()}).
              <button
                type="button"
                onClick={() => fetchSummary({ force: true, confirmSoftCap: true })}
                data-testid="lead-summary-confirm-cap"
                style={{ marginLeft: 8, padding: '4px 10px', background: '#D97706', border: 'none', borderRadius: 4, color: '#FFF', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
              >
                Generate anyway
              </button>
            </div>
          )}

          {summaryState === 'error' && (
            <div style={{ fontSize: 12, color: '#991B1B' }} data-testid="lead-summary-error">
              {summaryError}
            </div>
          )}

          {summaryState === 'ok' && summary && (
            <div data-testid="lead-summary-body" style={{ fontSize: 13, lineHeight: 1.55 }}>
              <div style={{ marginBottom: 6 }}><strong style={{ color: 'var(--admin-navy)' }}>Who:</strong> {summary.who}</div>
              <div style={{ marginBottom: 6 }}><strong style={{ color: 'var(--admin-navy)' }}>Funnel:</strong> {summary.funnel_position}</div>
              <div><strong style={{ color: 'var(--admin-gold)' }}>Next best action:</strong> {summary.next_best_action}</div>
              <div style={{ fontSize: 10, color: 'var(--admin-muted)', marginTop: 8 }}>
                {summary.cached ? 'cached · ' : 'fresh · '}
                {summary.model} · {fmtTs(summary.generated_at)}
              </div>
            </div>
          )}
        </div>

        {/* Primary CTA — draft follow-up */}
        <button
          type="button"
          onClick={() => setShowFollowup(true)}
          data-testid="lead-drawer-draft-followup"
          style={{
            width: '100%',
            marginTop: 14,
            padding: '12px 16px',
            background: 'var(--admin-navy-solid)',
            color: '#FAF8F3',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <PenLine size={16} />
          Draft follow-up with AI
        </button>

        {/* Lead facts */}
        <section style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--admin-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Details</div>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td style={td}>Status</td><td style={td}>{lead.status}</td></tr>
              <tr><td style={td}>Lead ID</td><td style={td}><code>{lead.lead_id}</code></td></tr>
              <tr><td style={td}>Advisor</td><td style={td}>{lead.assigned_advisor_name || '—'}</td></tr>
              <tr><td style={td}>Days in stage</td><td style={td}>{lead.days_in_status}</td></tr>
              <tr><td style={td}>Top products</td><td style={td}>{(lead.top_products || []).join(', ') || '—'}</td></tr>
            </tbody>
          </table>
        </section>

        {showFollowup && (
          <FollowupDraftModal
            leadId={lead.lead_id}
            leadName={lead.name}
            adminKey={adminKey}
            onClose={() => setShowFollowup(false)}
          />
        )}
      </aside>
    </>
  )
}

const td = { padding: '6px 0', borderBottom: '1px solid var(--admin-border-soft)', color: 'var(--admin-slate)' }
